import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { KybSubmissionDocument, DocumentType, KybSubmissionData } from 'apis/legitimizer/types';
import { Typography as T } from 'common/typography';

import MissingFieldError from './MissingFieldError';
import { KybProcessContext } from '../KybProcessContext';
import { SectionConfigurationContext } from '../SectionConfigurationContext';
import { useDocumentsFieldsConfigMemo } from '../useFieldsConfigMemo';

export type Props = {
  submissionDocuments: KybSubmissionDocument[];
  submissionData: KybSubmissionData;
  onEditData: (section: string) => void;
  setHasMissingFields?: (value: boolean) => void;
};

const Content = styled.div`
  text-align: start;
`;

const Legend = styled.legend`
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SecondaryAction = styled(Button).attrs({
  size: 'small',
  variant: 'secondary',
})``;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  & > li:not(:first-child) {
    margin-block-start: 16px;
  }
`;

const resolveDocuments = (documents: KybSubmissionDocument[], type: DocumentType) =>
  documents?.filter(document => document.documentType === type) || [];

const ReviewDocuments = ({ submissionDocuments, submissionData, onEditData, setHasMissingFields }: Props) => {
  const sectionConfiguration = useContext(SectionConfigurationContext);
  const { countryCode } = useContext(KybProcessContext);
  const documentsFieldsConfig = useDocumentsFieldsConfigMemo({ sectionConfiguration });
  const intl = useIntl();

  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const documentNames = resolveDocuments(submissionDocuments, 'REGISTRATION_CERTIFICATE').map(
    document => document.originalFilename,
  );

  const renderEmptyMessageOrError = () => {
    const isSoleTrader = submissionData.basicCompanyInfo?.businessDetails?.legalForm?.value === 'SOLE_TRADER';
    const isDocumentOptional = isSoleTrader && countryCode !== 'ISR';

    if (isDocumentOptional || !documentsFieldsConfig.registryExtractDocuments.required) {
      return t('compliance.kyb.sections.review.empty-documents');
    }

    return (
      <MissingFieldError
        shouldShow={documentsFieldsConfig.registryExtractDocuments.required}
        hasFieldErrorState={setHasMissingFields}
      />
    );
  };

  const documentsConfig = sectionConfiguration.documents?.documentDetails ?? [];

  if (!sectionConfiguration.enabledSections.includes('documents') || documentsConfig.length === 0) {
    return null;
  }

  return (
    <Content>
      <Legend>
        <T.Heading6>
          <FormattedMessage id="compliance.kyb.sections.documents.title" />
        </T.Heading6>
        <SecondaryAction type="button" onClick={() => onEditData('documents')}>
          <FormattedMessage id="state.edit" />
        </SecondaryAction>
      </Legend>

      {documentsFieldsConfig.registryExtractDocuments.visible && (
        <FormRow label={t('compliance.kyb.sections.documents.trade-registry-extract')} rightColStyle={{ minWidth: 0 }}>
          {documentNames.length > 0 ? (
            <List>
              {documentNames.map(name => (
                <li key={name}>{name}</li>
              ))}
            </List>
          ) : (
            renderEmptyMessageOrError()
          )}
        </FormRow>
      )}
    </Content>
  );
};

export default ReviewDocuments;
